import axios from 'axios';
import { API_CONFIG } from '../config/env';
import { sortByNewest } from '../utils/sortByNewest';

const APP_NOTIFICATIONS_CHANGED_EVENT = 'app-notifications-changed';

export interface AppNotification {
  id: number;
  transactionId?: number | null;
  type: string;
  severity: string;
  title: string;
  message: string;
  linkUrl?: string | null;
  createdAt: string;
  readAt?: string | null;
}

class AppNotificationService {
  private emitChanged() {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(APP_NOTIFICATIONS_CHANGED_EVENT));
    }
  }

  private getAuthHeaders() {
    const token = localStorage.getItem('accessToken');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  async getNotifications(take: number = 20, unreadOnly: boolean = false): Promise<AppNotification[]> {
    const response = await axios.get(`${API_CONFIG.BASE_URL}/Notifications`, {
      headers: this.getAuthHeaders(),
      params: {
        take,
        unreadOnly,
      },
    });

    return sortByNewest(
      Array.isArray(response.data) ? response.data : [],
      {
        dateKeys: ['createdAt'],
        numericFallbackKeys: ['id'],
      },
    );
  }

  async getUnreadCount(): Promise<number> {
    const response = await axios.get(`${API_CONFIG.BASE_URL}/Notifications/unread-count`, {
      headers: this.getAuthHeaders(),
    });

    return response.data?.unreadCount || 0;
  }

  async markAsRead(notificationId: number): Promise<void> {
    await axios.post(`${API_CONFIG.BASE_URL}/Notifications/${notificationId}/read`, {}, {
      headers: this.getAuthHeaders(),
    });
    this.emitChanged();
  }

  async markAllAsRead(): Promise<void> {
    await axios.post(`${API_CONFIG.BASE_URL}/Notifications/read-all`, {}, {
      headers: this.getAuthHeaders(),
    });
    this.emitChanged();
  }

  async markTransactionNotificationsAsRead(transactionId: number): Promise<void> {
    await axios.post(`${API_CONFIG.BASE_URL}/Notifications/transactions/${transactionId}/read`, {}, {
      headers: this.getAuthHeaders(),
    });
    this.emitChanged();
  }
}

export const appNotificationService = new AppNotificationService();
export { APP_NOTIFICATIONS_CHANGED_EVENT };
